import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { apiGet } from "../../shared/apiClient";
import { fetchOrderDetailsList, fetchOrdersByCustomerId, saveOrder } from "../../shared/commerceMastersApi";
import { normalizeOrderItemStatus } from "../../shared/orderItemStatuses";
import { normalizePaymentStatus, PAYMENT_STATUSES } from "../../shared/paymentStatuses";
import { resolveMediaUrl } from "../../shared/mediaUrl";
import { IconEye, IconList, IconRefresh } from "../../shared/toolbarIcons";
import { mastersDataGridSx } from "../../shared/mastersDataGridSx";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "../../shared/useClientPagination";

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function formatMoney(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function ProductThumb({ image, name, size = 36 }) {
  const src = resolveMediaUrl(image);
  if (!src) {
    return (
      <div className="order-product-thumb order-product-thumb--empty" style={{ width: size, height: size }} title={name || "Product"}>
        —
      </div>
    );
  }
  return <img src={src} alt={name || "Product"} className="order-product-thumb" style={{ width: size, height: size }} />;
}

function OrderProductsCell({ lines }) {
  if (!lines?.length) return <span>—</span>;
  const visible = lines.slice(0, 2);
  const extra = lines.length - visible.length;
  return (
    <div className="order-products-cell">
      {visible.map((line) => (
        <div key={line.id} className="order-products-cell__item" title={`${line.productName} · ${line.itemStatus}`}>
          <ProductThumb image={line.productImage} name={line.productName} />
          <span className="order-products-cell__name">
            {line.productName}
            <span className="order-products-cell__status"> · {line.itemStatus}</span>
          </span>
        </div>
      ))}
      {extra > 0 && <span className="order-products-cell__more">+{extra} more</span>}
    </div>
  );
}

function OrderPaymentStatusCell({ order, onStatusChange, savingOrderId }) {
  const paymentStatus = normalizePaymentStatus(order.paymentStatus ?? order.PaymentStatus);
  const orderId = order.id ?? order.Id;
  return (
    <div className="order-item-status-cell" onClick={(e) => e.stopPropagation()}>
      <div className="order-item-status-cell__select-wrap">
        <select
          className="order-item-status-cell__select"
          value={paymentStatus}
          disabled={savingOrderId === orderId}
          aria-label={`Payment status for order ${order.orderNo ?? order.OrderNo ?? orderId}`}
          onChange={(e) => onStatusChange(order, e.target.value)}
        >
          {PAYMENT_STATUSES.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

export function CustomerOrdersPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const customerParam = searchParams.get("customer");
  const customerId = Number(customerParam);

  const [customerMeta, setCustomerMeta] = useState(null);
  const [products, setProducts] = useState([]);
  const [orderDetails, setOrderDetails] = useState([]);
  const [items, setItems] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentSavingId, setPaymentSavingId] = useState(null);

  const customerValid = Boolean(customerParam) && Number.isFinite(customerId) && customerId > 0;

  const reload = useCallback(async () => {
    if (!customerValid) return;
    setListLoading(true);
    const res = await fetchOrdersByCustomerId(customerId);
    setListLoading(false);
    if (!res.ok) {
      setError(res.error ?? "Failed to load orders");
      setItems([]);
      return;
    }
    setError(null);
    setItems(res.items);
  }, [customerId, customerValid]);

  useEffect(() => {
    if (!customerValid) return;
    let cancelled = false;
    (async () => {
      const [cRes, pRes, dRes] = await Promise.all([
        apiGet(`/api/Customers/${customerId}`),
        apiGet("/api/Products"),
        fetchOrderDetailsList()
      ]);
      if (cancelled) return;
      if (cRes.ok) setCustomerMeta(cRes.data);
      if (pRes.ok) setProducts(pRes.data ?? []);
      if (dRes.ok) setOrderDetails(dRes.items);
    })();
    return () => {
      cancelled = true;
    };
  }, [customerId, customerValid]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const handlePaymentStatusChange = useCallback(async (order, newStatus) => {
    const paymentStatus = normalizePaymentStatus(newStatus);
    const orderId = order.id ?? order.Id;
    if (paymentStatus === normalizePaymentStatus(order.paymentStatus ?? order.PaymentStatus)) return;

    setPaymentSavingId(orderId);
    setError(null);
    const res = await saveOrder({ ...order, id: orderId, paymentStatus });
    setPaymentSavingId(null);

    if (!res.ok) {
      setError(res.error ?? "Failed to update payment status");
      return;
    }

    setItems((prev) =>
      prev.map((item) => {
        const id = item.id ?? item.Id;
        if (Number(id) !== Number(orderId)) return item;
        return { ...item, paymentStatus, PaymentStatus: paymentStatus };
      })
    );
  }, []);

  const customerLabel = customerMeta?.name ?? `Customer #${customerId}`;

  const productById = useMemo(() => {
    const map = new Map();
    for (const product of products) map.set(product.id ?? product.Id, product);
    return map;
  }, [products]);

  const orderDetailsByOrderId = useMemo(() => {
    const map = new Map();
    for (const detail of orderDetails) {
      const orderId = detail.orderId ?? detail.OrderId;
      const list = map.get(orderId) ?? [];
      list.push(detail);
      map.set(orderId, list);
    }
    return map;
  }, [orderDetails]);

  const rows = useMemo(
    () =>
      items.map((order) => {
        const orderId = order.id ?? order.Id;
        const lines = (orderDetailsByOrderId.get(orderId) ?? []).map((line) => {
          const productId = line.productId ?? line.ProductId;
          const product = productById.get(productId);
          return {
            id: line.id ?? line.Id,
            productName: product?.name ?? product?.Name ?? (productId != null ? `Product #${productId}` : "—"),
            productImage: product?.image ?? product?.Image ?? "",
            itemStatus: normalizeOrderItemStatus(line.itemStatus ?? line.ItemStatus)
          };
        });
        return {
          id: orderId,
          order,
          productLines: lines,
          orderNo: order.orderNo ?? order.OrderNo ?? `#${orderId}`,
          date: formatDateTime(order.date ?? order.Date),
          paymentStatus: normalizePaymentStatus(order.paymentStatus ?? order.PaymentStatus),
          paymentMethod: order.paymentMethod ?? order.PaymentMethod ?? "—",
          grandTotal: formatMoney(order.grandTotal ?? order.GrandTotal)
        };
      }),
    [items, orderDetailsByOrderId, productById]
  );

  const columns = useMemo(
    () => [
      { field: "orderNo", headerName: "Order no", flex: 0.9, minWidth: 120 },
      { field: "date", headerName: "Date", flex: 1, minWidth: 150 },
      {
        field: "products",
        headerName: "Products",
        flex: 1.2,
        minWidth: 180,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => <OrderProductsCell lines={params.row.productLines} />
      },
      {
        field: "paymentStatus",
        headerName: "Pay status",
        minWidth: 130,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <OrderPaymentStatusCell
            order={params.row.order}
            savingOrderId={paymentSavingId}
            onStatusChange={handlePaymentStatusChange}
          />
        )
      },
      { field: "paymentMethod", headerName: "Pay method", minWidth: 110 },
      { field: "grandTotal", headerName: "Grand total", minWidth: 110 },
      {
        field: "actions",
        headerName: "Actions",
        width: 90,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <div className="table-actions">
            <button
              className="icon-button"
              type="button"
              title="View order"
              aria-label="View order"
              onClick={() => navigate(`/orders?edit=${params.row.id}`)}
            >
              <IconEye />
            </button>
          </div>
        )
      }
    ],
    [navigate, handlePaymentStatusChange, paymentSavingId]
  );

  if (!customerValid) {
    return <Navigate to="/customers-list" replace />;
  }

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>Orders</h2>
          <p>{customerLabel}</p>
        </div>
        <div className="card-header-actions">
          <button
            className="icon-button icon-button--toolbar"
            type="button"
            onClick={() => void reload()}
            disabled={listLoading}
            title="Refresh"
            aria-label="Refresh"
          >
            <IconRefresh />
          </button>
          <Link to="/customers-list" className="icon-button icon-button--toolbar" title="Customers list" aria-label="Back to customers list">
            <IconList />
          </Link>
        </div>
      </div>
      <div className="card-body">
        {error && <div className="error-banner">{error}</div>}
        <div className="masters-data-grid-shell">
          <DataGrid
            rows={rows}
            columns={columns}
            loading={listLoading}
            disableRowSelectionOnClick
            pagination
            paginationMode="client"
            pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: DEFAULT_PAGE_SIZE_OPTIONS[0] } }
            }}
            sx={mastersDataGridSx}
          />
        </div>
      </div>
    </div>
  );
}
